import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import {
  getCurrentCompanyLink,
  linkWithCompany,
  unlinkFromCompany,
  validateCompanyCode,
} from "@/services/subscriptionService";
import { toast } from "@/hooks/use-toast";

export type { CompanyLinkCurrent } from "@/services/subscriptionService";

export interface UseCompanyLinkOptions {
  /** When false, skips fetching (e.g. profile modal closed). */
  enabled?: boolean;
}

/**
 * Fetches current company link and provides link/unlink actions.
 * Invalidates subscription query on link/unlink so premium status updates.
 */
export function useCompanyLink(options?: UseCompanyLinkOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: companyLink,
    isLoading: isLoadingLink,
    refetch: refetchLink,
  } = useQuery({
    queryKey: ["companyLink", user?.id],
    queryFn: () => (user?.id ? getCurrentCompanyLink(user.id) : Promise.resolve(null)),
    enabled: !!user?.id && (options?.enabled ?? true),
    staleTime: 60 * 1000,
  });

  const linkMutation = useMutation({
    mutationFn: (code: string) => linkWithCompany(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companyLink", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["subscription", user?.id] });
      toast({
        title: "Linked to company",
        description: `Your Premium is now sponsored by your company.`,
        variant: "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Link failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: () => unlinkFromCompany(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companyLink", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["subscription", user?.id] });
      toast({
        title: "Unlinked from company",
        description: "Your account and data remain. You can link again with a new code.",
        variant: "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Unlink failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  /**
   * Validate Code.
   */
  const validateCode = async (
    code: string
  ): Promise<{ company_name: string; company_slug: string } | null> => {
    try {
      return await validateCompanyCode(code);
    } catch (error) {
      toast({
        title: "Invalid code",
        description: error instanceof Error ? error.message : "Code not found or expired.",
        variant: "destructive",
      });
      return null;
    }
  };

  return {
    companyLink: companyLink ?? null,
    isLoadingLink,
    refetchLink,
    linkWithCompany: linkMutation.mutateAsync,
    unlinkFromCompany: unlinkMutation.mutateAsync,
    isLinking: linkMutation.isPending,
    isUnlinking: unlinkMutation.isPending,
    validateCode,
  };
}
